import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ExpenditureSub = () => {
  const [state, setState] = useState({
    kitchenId: null,
    selectedDate: new Date().toISOString().split("T")[0],
    stockItems: [],
    subKitchens: [],
    selectedKitchen: "",
    selectedItemId: "",
    quantity: "",
    price: "", // Added price field
    remarks: "",
    selectedItems: [],
    isSaving: false,
    totalAmount: 0, // Added total amount
    mealType: ""
  });

  const navigate = useNavigate();

  // Update state helper
  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  // Calculate total amount
  const calculateTotalAmount = useCallback((items) => {
    return items.reduce((total, item) => total + (item.totalPrice || 0), 0);
  }, []);

  // Fetch sub-kitchens
  const fetchSubKitchens = useCallback(async (kitchenId) => {
    try {
      const res = await axios.get(
        `https://api-ims.indevconsultancy.in/sub-kichange-get.php?kitchen_id=${kitchenId}`
      );
      const subKitchens = res.data.status === "success"
        ? res.data.data.map(item => ({
          id: item.subkichen_id,
          kitchenName: item.subKitchen_name,
          status: item.status === "1" ? "Active" : "Inactive",
        }))
        : [];
      updateState({ subKitchens });
    } catch (err) {
      console.error("Error fetching sub-kitchens:", err);
      updateState({ subKitchens: [] });
    }
  }, []);

  // Fetch stock items
  const fetchStockItems = useCallback(async (kitchenId, selectedKitchen) => {
    if (!kitchenId || !selectedKitchen) {
      updateState({ stockItems: [] });
      return;
    }

    const KitchenType = localStorage.getItem("KitchenType");
    if (!KitchenType) return;

    try {
      const response = await axios.get(
        `https://api-ims.indevconsultancy.in/sub-stoct-list.php?kitchen_id=${kitchenId}&subkichenId=${selectedKitchen}&KitchenType=${KitchenType}`
      );

      const stockItems = response.data.status === "success"
        ? response.data.data.map(item => ({
          id: parseInt(item.items_id),
          itemName: item.stock_name,
          availableStock: item.quantity,
          price: parseFloat(item.price) || 0,
        }))
        : [];
      updateState({ stockItems });
    } catch (error) {
      console.error("Stock fetch error:", error);
      updateState({ stockItems: [] });
    }
  }, []);

  // Initialize component
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.kitchen) {
      updateState({ kitchenId: user.kitchen });
      fetchSubKitchens(user.kitchen);
    }
  }, [fetchSubKitchens]);

  // Fetch stock when kitchen or sub-kitchen changes
  useEffect(() => {
    if (state.kitchenId && state.selectedKitchen) {
      fetchStockItems(state.kitchenId, state.selectedKitchen);
    }
  }, [state.kitchenId, state.selectedKitchen, fetchStockItems]);

  // Update total amount when selected items change
  useEffect(() => {
    const total = calculateTotalAmount(state.selectedItems);
    updateState({ totalAmount: total });
  }, [state.selectedItems, calculateTotalAmount]);

  // Set price when item is selected
  useEffect(() => {
    if (state.selectedItemId && state.stockItems.length > 0) {
      const selected = state.stockItems.find(item => item.id === parseInt(state.selectedItemId));
      if (selected) {
        updateState({ price: selected.price.toString() });
      }
    } else {
      updateState({ price: "" });
    }
  }, [state.selectedItemId, state.stockItems]);

  // Validation helpers
  const validateItem = () => {
    const { selectedItemId, quantity, price, stockItems, selectedItems, mealType } = state;
    const qty = parseFloat(quantity);
    const itemPrice = parseFloat(price);
    const selected = stockItems.find(item => item.id === parseInt(selectedItemId));

    if (!mealType) return "Please select meal type (Breakfast / Lunch / Dinner).";
    if (!selectedItemId || !selected) return "Please select a valid item.";
    if (selected.availableStock <= 0) return "This item has no available stock.";
    if (isNaN(qty) || qty <= 0) return "Please enter a valid quantity.";
    if (isNaN(itemPrice) || itemPrice < 0) return "Please enter a valid price.";
    if (qty > selected.availableStock)
      return `Quantity exceeds available stock (${selected.availableStock}).`;
    if (selectedItems.find(item => item.kitchestock === parseInt(selectedItemId)))
      return "This item is already added.";

    return null;
  };

  // Add item to list
  const handleAddItem = () => {
    const error = validateItem();
    if (error) {
      alert(error);
      return;
    }

    const { selectedItemId, quantity, price, remarks, selectedKitchen, kitchenId, selectedDate, stockItems, selectedItems, mealType } = state;
    const selected = stockItems.find(item => item.id === parseInt(selectedItemId));
    const qty = parseFloat(quantity);
    const itemPrice = parseFloat(price);
    const totalPrice = qty * itemPrice;

    const newItem = {
      kitchestock: selected.id,
      itemName: selected.itemName,
      expenditure_quantity: qty,
      price: itemPrice,
      totalPrice: totalPrice,
      remarks: remarks.trim(),
      sub_kitchen_id: selectedKitchen,
      kitchen_id: kitchenId,
      add_date: selectedDate,
      meal_type: mealType,
      availableStock: selected.availableStock,
    };

    updateState({
      selectedItems: [...selectedItems, newItem],
      selectedItemId: "",
      quantity: "",
      price: "",
      remarks: ""
    });
  };

  // Remove item from list
  const handleRemoveItem = (index) => {
    const updatedItems = state.selectedItems.filter((_, i) => i !== index);
    updateState({ selectedItems: updatedItems });
  };

  // Submit all items
  const handleSubmitAll = async () => {
    const { selectedItems } = state;

    if (selectedItems.length === 0) {
      alert("Please add at least one item.");
      return;
    }

    const KitchenType = localStorage.getItem("KitchenType");
    if (!KitchenType) return;

    updateState({ isSaving: true });

    try {
      const payload = selectedItems.map(({ itemName, availableStock, totalPrice, ...rest }) => ({
        ...rest,
        KitchenType
      }));
      
      await axios.post(
        "https://api-ims.indevconsultancy.in/sub-expenditures-insert.php",
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      navigate("/ExpenditureListSubKitchen");

    } catch (err) {
      console.error("Submission error:", err);
      alert("Failed to submit. Please try again.");
    } finally {
      updateState({ isSaving: false });
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    updateState({ [field]: value });
  };

  const {
    selectedDate, subKitchens, selectedKitchen, stockItems, selectedItemId,
    quantity, price, remarks, selectedItems, isSaving, totalAmount,
    mealType
  } = state;

  return (
    <div className="container mt-4">
      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0 text-white">Daily Expenditure Entry</h5>
        </div>

        <div className="card-body">
          {/* Filters */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Date</label>
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => handleInputChange('selectedDate', e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className="col-lg-4">
              <label className="form-label fw-semibold">Sub Kitchen</label>
              <select
                className="form-select"
                value={selectedKitchen}
                onChange={(e) => handleInputChange('selectedKitchen', e.target.value)}
              >
                <option value="">Select Sub Kitchen</option>
                {subKitchens.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.kitchenName}
                  </option>
                ))}
              </select>
            </div>

            {/* Meal Type */}
            <div className="col-lg-4">
              <label className="form-label fw-semibold">Meal Type</label>
              <select
                className="form-select"
                value={mealType}
                onChange={(e) => handleInputChange('mealType', e.target.value)}
              >
                <option value="">Select Meal Type</option>
                <option value="Breakfast">Breakfast</option>
                <option value="Lunch">Lunch</option>
                <option value="Dinner">Dinner</option>
              </select>
            </div>
          </div>

          {/* Item Selection */}
          {selectedKitchen && (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Select Item</label>
                  <select
                    className="form-select"
                    value={selectedItemId}
                    onChange={(e) => handleInputChange('selectedItemId', e.target.value)}
                  >
                    <option value="">Select Item</option>
                    {stockItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.itemName} | Available: {item.availableStock} | Price: ₹{item.price}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Quantity, Price & Remarks */}
              {selectedItemId && (
                <div className="row g-3 mb-3">
                  <div className="col-md-2">
                    <label className="form-label fw-semibold">Quantity</label>
                    <input
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter quantity"
                      value={quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold">Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter price"
                      value={price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                    />
                  </div>
                  {quantity && price && (
                    <div className="col-md-2">
                      <label className="form-label fw-semibold">Total</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={`₹${(parseFloat(quantity) * parseFloat(price) || 0).toFixed(2)}`}
                        readOnly
                        disabled
                      />
                    </div>
                  )}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Remarks</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter remarks (optional)"
                      value={remarks}
                      onChange={(e) => handleInputChange('remarks', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      className="btn btn-info w-100"
                      onClick={handleAddItem}
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Items Table */}
          {selectedItems.length > 0 && (
            <>
              <div className="table-responsive mt-4">
                <table className="table table-bordered table-hover align-middle text-center">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Meal</th>
                      <th>Available</th>
                      <th>Quantity</th>
                      <th>Price (₹)</th>
                      <th>Total (₹)</th>
                      <th>Remarks</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((item, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="text-start">{item.itemName}</td>
                        <td>{item.meal_type || "-"}</td>
                        <td>
                          <span className="badge bg-secondary">
                            {item.availableStock}
                          </span>
                        </td>
                        <td>{item.expenditure_quantity}</td>
                        <td>₹{item.price.toFixed(2)}</td>
                        <td className="fw-bold">₹{item.totalPrice.toFixed(2)}</td>
                        <td>{item.remarks || "-"}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveItem(i)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr>
                      <td colSpan="6" className="text-end fw-bold">Grand Total:</td>
                      <td className="fw-bold">₹{totalAmount.toFixed(2)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className=" justify-content-between align-items-end mt-3 text-end">
             
                <button
                  className="btn btn-success px-4"
                  onClick={handleSubmitAll}
                  disabled={isSaving}
                >
                  {isSaving ? "Submitting..." : "Submit All"}
                </button>
              </div>
            </>
          )}

          {/* Empty State */}
          {selectedKitchen && selectedItems.length === 0 && (
            <div className="text-center text-muted py-4">
              <p>No items added yet. Select an item and quantity to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenditureSub;